import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, afterNextRender, inject, signal } from '@angular/core';

export type ThemeOption = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'lumina-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private mediaQuery: MediaQueryList | null = null;

  readonly preference = signal<ThemeOption>('light');
  readonly resolved = signal<'light' | 'dark'>('light');

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const stored = this.readStored();
      this.preference.set(stored);
      this.apply(stored);
      this.watchSystem();
    });
  }

  setTheme(theme: ThemeOption) {
    this.preference.set(theme);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, theme);
      this.apply(theme);
    }
  }

  private readStored(): ThemeOption {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }

    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }

    return 'light';
  }

  private apply(theme: ThemeOption) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const resolved = theme === 'system' ? this.systemPreference() : theme;
    this.resolved.set(resolved);

    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.style.colorScheme = resolved;
    root.dataset['theme'] = theme;
  }

  private systemPreference(): 'light' | 'dark' {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private watchSystem() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQuery.addEventListener('change', () => {
      if (this.preference() === 'system') {
        this.apply('system');
      }
    });
  }
}
